import React, { useEffect, useMemo, useRef, useState } from "react";
import Title from "../layouts/Title";
import ContactLeft from "./ContactLeft";
import Turnstile from "react-turnstile";

const MAX_MESSAGE_LENGTH = 2000;

// ✅ Microsoft Bookings – 30 min intro call
const BOOKINGS_30_MIN_URL =
    "https://outlook.office.com/bookwithme/user/b6853e19f4c84b4f912d9d53eaad07b9@cesaragudelo.com/meetingtype/skrH_ZGwoEirPpfWgK5Irw2?bookingcode=985379df-2b11-410d-9f0e-fd9a0173070e&anonymous&ismsaljsauthenabled&ep=mlink";

const Contact = () => {
    const [username, setUsername] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [turnstileToken, setTurnstileToken] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [isSending, setIsSending] = useState(false);

    const successRef = useRef(null);

    // Track touch/blur so we don't show errors on first render
    const [touched, setTouched] = useState({
        username: false,
        phoneNumber: false,
        email: false,
        subject: false,
        message: false,
    });

    const markTouched = (field) =>
        setTouched((prev) => ({ ...prev, [field]: true }));

    const isValidEmail = (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value).trim());

    // Field-level errors
    const errors = useMemo(() => {
        const e = {};

        if (!username.trim()) e.username = "Username is required!";

        if (!phoneNumber.trim()) e.phoneNumber = "Phone number is required!";
        else if (!/^[0-9+\-\s()]{7,}$/.test(phoneNumber.trim()))
            e.phoneNumber = "Enter a valid phone number.";

        if (!email.trim()) e.email = "Please give your Email!";
        else if (!isValidEmail(email)) e.email = "Give a valid Email!";

        if (!subject.trim()) e.subject = "Please give your Subject!";

        if (!message.trim()) e.message = "Message is required!";
        else if (message.length > MAX_MESSAGE_LENGTH)
            e.message = `Message must be ${MAX_MESSAGE_LENGTH} characters or less.`;

        if (!turnstileToken) e.turnstileToken = "Please verify you are human.";

        return e;
    }, [username, phoneNumber, email, subject, message, turnstileToken]);

    const isFormValid = useMemo(
        () => Object.keys(errors).length === 0,
        [errors]
    );

    const showError = (field) => touched[field] && errors[field];

    // Focus + scroll to confirmation message after success
    useEffect(() => {
        if (!successMsg) return;

        requestAnimationFrame(() => {
            successRef.current?.focus?.();
            successRef.current?.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
            });
        });
    }, [successMsg]);

    const handleSend = async (e) => {
        e.preventDefault();

        setTouched({
            username: true,
            phoneNumber: true,
            email: true,
            subject: true,
            message: true,
        });

        if (!isFormValid) return;

        const apiUrl = process.env.REACT_APP_CONTACT_API_URL;
        if (!apiUrl) return;

        try {
            setIsSending(true);

            const res = await fetch(apiUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: username,
                    phone: phoneNumber,
                    email,
                    subject,
                    message,
                    turnstileToken,
                }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                alert(data?.error || "Could not send message. Please try again.");
                return;
            }

            setSuccessMsg(
                `Thank you dear ${username}, your message has been sent successfully!`
            );

            // Reset form
            setUsername("");
            setPhoneNumber("");
            setEmail("");
            setSubject("");
            setMessage("");
            setTurnstileToken("");
            setTouched({
                username: false,
                phoneNumber: false,
                email: false,
                subject: false,
                message: false,
            });
        } catch {
            alert("Network error. Please try again.");
        } finally {
            setIsSending(false);
        }
    };

    const apiUrl = process.env.REACT_APP_CONTACT_API_URL;
    const apiNotConfigured = !apiUrl;

    const remaining = MAX_MESSAGE_LENGTH - message.length;
    const isNearLimit = remaining <= 100;

    return (
        <section
            id="contact"
            className="w-full py-20 border-b-[1px] border-b-black"
        >
            <div className="flex flex-col items-center text-center gap-3">
                <Title title="CONTACT" des="Contact With Me" />
            </div>

            <div className="w-full mt-10">
                <div className="w-full h-auto flex flex-col lgl:flex-row justify-between">
                    <ContactLeft />

                    <div className="w-full lgl:w-[60%] h-full py-10 bg-gradient-to-r from-[#1e2024] to-[#23272b] flex flex-col gap-8 p-4 lgl:p-8 rounded-lg shadow-shadowOne">
                        {/* ✅ SUCCESS BANNER */}
                        {successMsg && (
                            <div
                                ref={successRef}
                                tabIndex={-1}
                                role="status"
                                aria-live="polite"
                                className="w-full py-4 px-4 lgl:px-6 bg-gradient-to-r from-[#1e2024] to-[#23272b] shadow-shadowOne text-center text-green-500 text-base lgl:text-lg tracking-wide rounded-md outline-none break-words"
                            >
                                {successMsg}
                            </div>
                        )}

                        {/* ✅ BOOKING CTA (30 min only) */}
                        <div className="w-full p-4 lgl:p-6 rounded-lg shadow-shadowOne bg-[#141518] border border-gray-700/40">
                            <p className="text-sm text-gray-300 uppercase tracking-wide">
                                Recruiters / Hiring Managers
                            </p>

                            <h3 className="text-lg lgl:text-xl font-semibold text-white mt-1">
                                Schedule a 30-minute intro call
                            </h3>

                            <p className="text-sm text-gray-400 mt-2">
                                Book a time to discuss QA Automation / SDET services,
                                availability, and next steps. You’ll receive a confirmation
                                email with the Microsoft Teams link.
                            </p>

                            <div className="mt-4">
                                <a
                                    href={BOOKINGS_30_MIN_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="w-full sm:w-auto text-center px-6 h-12 flex items-center justify-center rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold tracking-wide duration-300"

                                >
                                    Schedule 30-min Intro Call
                                </a>
                            </div>

                            <p className="text-xs text-gray-500 mt-3">
                                Prefer email? You can also send a message using the form below.
                            </p>
                        </div>

                        {/* ✅ CONTACT FORM */}
                        <form
                            onSubmit={handleSend}
                            className="w-full flex flex-col gap-4 lgl:gap-6 py-2 lgl:py-5"
                            noValidate
                        >
                            {apiNotConfigured && (
                                <p className="py-3 bg-gradient-to-r from-[#1e2024] to-[#23272b] shadow-shadowOne text-center text-orange-500 text-base tracking-wide">
                                    Contact service is not configured (missing API URL).
                                </p>
                            )}

                            <div className="w-full flex flex-col lgl:flex-row gap-10">
                                <div className="w-full lgl:w-1/2 flex flex-col gap-2">
                                    <p className="text-sm text-gray-400 uppercase tracking-wide">
                                        Your name
                                    </p>
                                    <input
                                        onChange={(e) => setUsername(e.target.value)}
                                        onBlur={() => markTouched("username")}
                                        value={username}
                                        className={`contactInput ${
                                            showError("username") ? "outline-designColor" : ""
                                        }`}
                                        type="text"
                                    />
                                    {showError("username") && (
                                        <p className="text-xs text-orange-400">
                                            {errors.username}
                                        </p>
                                    )}
                                </div>

                                <div className="w-full lgl:w-1/2 flex flex-col gap-2">
                                    <p className="text-sm text-gray-400 uppercase tracking-wide">
                                        Phone Number
                                    </p>
                                    <input
                                        onChange={(e) => setPhoneNumber(e.target.value)}
                                        onBlur={() => markTouched("phoneNumber")}
                                        value={phoneNumber}
                                        className={`contactInput ${
                                            showError("phoneNumber")
                                                ? "outline-designColor"
                                                : ""
                                        }`}
                                        type="text"
                                    />
                                    {showError("phoneNumber") && (
                                        <p className="text-xs text-orange-400">
                                            {errors.phoneNumber}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-col gap-2">
                                <p className="text-sm text-gray-400 uppercase tracking-wide">
                                    Email
                                </p>
                                <input
                                    onChange={(e) => setEmail(e.target.value)}
                                    onBlur={() => markTouched("email")}
                                    value={email}
                                    className={`contactInput ${
                                        showError("email") ? "outline-designColor" : ""
                                    }`}
                                    type="email"
                                />
                                {showError("email") && (
                                    <p className="text-xs text-orange-400">{errors.email}</p>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <p className="text-sm text-gray-400 uppercase tracking-wide">
                                    Subject
                                </p>
                                <input
                                    onChange={(e) => setSubject(e.target.value)}
                                    onBlur={() => markTouched("subject")}
                                    value={subject}
                                    className={`contactInput ${
                                        showError("subject") ? "outline-designColor" : ""
                                    }`}
                                    type="text"
                                />
                                {showError("subject") && (
                                    <p className="text-xs text-orange-400">
                                        {errors.subject}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm text-gray-400 uppercase tracking-wide">
                                        Message
                                    </p>
                                    <p
                                        className={`text-xs ${
                                            isNearLimit ? "text-orange-400" : "text-gray-500"
                                        }`}
                                        aria-live="polite"
                                    >
                                        {message.length}/{MAX_MESSAGE_LENGTH}
                                    </p>
                                </div>

                                <textarea
                                    onChange={(e) => setMessage(e.target.value)}
                                    onBlur={() => markTouched("message")}
                                    value={message}
                                    maxLength={MAX_MESSAGE_LENGTH}
                                    className={`contactTextArea ${
                                        showError("message") ? "outline-designColor" : ""
                                    }`}
                                    cols="30"
                                    rows="8"
                                />

                                <p className="text-xs text-gray-500">
                                    Max {MAX_MESSAGE_LENGTH} characters.
                                </p>

                                {showError("message") && (
                                    <p className="text-xs text-orange-400">
                                        {errors.message}
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col gap-2">
                                <Turnstile
                                    sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                                    onSuccess={(token) => setTurnstileToken(token)}
                                    onExpire={() => setTurnstileToken("")}
                                    onError={() => setTurnstileToken("")}
                                />
                                {!turnstileToken && (
                                    <p className="text-xs text-gray-500">
                                        Please complete the verification to enable sending.
                                    </p>
                                )}
                            </div>

                            <div className="w-full">
                                <button
                                    type="submit"
                                    disabled={isSending || !isFormValid || apiNotConfigured}
                                    className="w-full h-12 bg-[#141518] rounded-lg text-base text-gray-400 tracking-wider uppercase hover:text-white duration-300 hover:border-[1px] hover:border-designColor border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isSending ? "Sending..." : "Send Message"}
                                </button>

                                {!isFormValid && (
                                    <p className="text-xs text-gray-500 text-center mt-2">
                                        Please complete the required fields to enable sending.
                                    </p>
                                )}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
